import React, { useState, useEffect } from 'react';
import axios from 'axios';

import { ethers } from 'ethers'; // Changed from require to import
import API_CONFIG from '../config/api';

const BridgeForm = ({ chains, account, walletType, onTransactionSubmit }) => {
  const [fromChain, setFromChain] = useState('');
  const [toChain, setToChain] = useState('');
  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState(account);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [balance, setBalance] = useState('0.0');
  const [toBalance, setToBalance] = useState('0.0'); // Add this line

  useEffect(() => {
    setRecipient(account);
    if (account && fromChain) {
      fetchBalance(fromChain, account);
    }
  }, [account]);

  // Add this useEffect to fetch balance when fromChain changes
  useEffect(() => {
    if (account && fromChain) {
      fetchBalance(fromChain, account);
    } else {
      setBalance('0.0');
    }
  }, [fromChain]);

  // Add this useEffect to fetch toChain balance when it changes
  useEffect(() => {
    if (account && toChain) {
      fetchToBalance(toChain, account);
    } else {
      setToBalance('0.0');
    }
  }, [toChain]);

  const fetchBalance = async (chainKey, address) => {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/api/balance/${chainKey}/${address}`);
      setBalance(response.data.tokenBalance); // or nativeBalance if you want to show native token
    } catch (error) {
      console.error('Error fetching balance:', error);
      setBalance('0.0');
    }
  };

  const fetchToBalance = async (chainKey, address) => {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/api/balance/${chainKey}/${address}`);
      setToBalance(response.data.tokenBalance); // or nativeBalance if you want to show native token
    } catch (error) {
      console.error('Error fetching balance:', error);
      setToBalance('0.0');
    }
  };

  const getQuote = async () => {
    if (!fromChain || !toChain || !amount || !recipient) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/api/quote`, {
        fromChain,
        toChain,
        amount,
        recipient
      });

      setQuote(response.data);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to get quote');
    } finally {
      setLoading(false);
    }
  };

// Fixed Bridge.js execute transfer function

const executeTransfer = async () => {
  if (!quote) return;
  
  setLoading(true);
  setError('');
  
  try {
    const sender = account; // From props
    
    // Verify the sender matches the connected wallet
    let currentProvider;
    console.log("walletType   : ",walletType);
    if (walletType === 'MetaMask') {
      if (!window.ethereum?.isMetaMask) {
        throw new Error('MetaMask not detected');
      }
      const providers = window.ethereum.providers || [window.ethereum];
      const metamaskProvider = providers.find(provider => provider.isMetaMask && !provider.isPhantom);
      currentProvider = metamaskProvider;
    } 
    else if (walletType === 'Phantom') {
      const providers = window.phantom?.providers || [window.ethereum];
      const phantomProvider = providers.find(provider => !provider.isMetaMask && provider.isPhantom);
      currentProvider = phantomProvider;
      if (!currentProvider?.isPhantom) {
        throw new Error('Phantom Ethereum wallet not detected');
      }
    }
    
    // Verify the account is still connected
    const provider = new ethers.BrowserProvider(currentProvider);
    const signer = await provider.getSigner();
    const currentAddress = await signer.getAddress();
    
    if (currentAddress.toLowerCase() !== sender.toLowerCase()) {
      throw new Error('Connected account does not match selected account');
    }

    // ⭐ NEW: Verify the wallet's selected chain matches the source chain
    const selectedChainId = await currentProvider.request({ method: 'eth_chainId' });
    const sourceChainConfig = chains.find(chain => chain.key === fromChain);
    
    if (!sourceChainConfig) {
      throw new Error('Invalid source chain configuration');
    }

    // Convert chainId to hex for comparison (MetaMask returns hex)
    const expectedChainIdHex = `0x${sourceChainConfig.chainId.toString(16)}`;
    
    if (selectedChainId !== expectedChainIdHex) {
      throw new Error(`Please switch your wallet to ${sourceChainConfig.name} (Chain ID: ${sourceChainConfig.chainId}) before proceeding`);
    }

    // Prepare transaction data
    const txData = {
      fromChain,
      toChain,
      amount,
      recipient,
      sender,
      walletType
    };
    
    // Send to backend
    const response = await axios.post(`${API_CONFIG.BASE_URL}/api/transfer`, txData);
    const { txDataToSign, explorer } = response.data;
    
    // Send transaction through the correct provider
    const txHash = await currentProvider.request({
      method: 'eth_sendTransaction',
      params: [txDataToSign]
    });
    
    // Update UI
    onTransactionSubmit({
      txHash,
      explorer,
      status: 'pending',
      fromChain,
      toChain,
      amount
    });
    
    // Wait for confirmation
    waitForTransactionConfirmation(txHash, explorer);
    
    setAmount('');
    setQuote(null);
    
  } catch (error) {
    console.error('Transfer error:', error);
    setError(error.message || 'Transfer failed');
  } finally {
    setLoading(false);
  }
};


// Helper function to wait for transaction confirmation
const waitForTransactionConfirmation = async (txHash, explorer) => {
  try {
    // Wait for confirmation via backend
    const confirmResponse = await axios.post(`${API_CONFIG.BASE_URL}/api/wait-confirmation`, {
      txHash,
      fromChain
    });
    
    if (confirmResponse.data.success) {
      console.log('Transaction confirmed:', confirmResponse.data);
      // Update transaction status to confirmed
      onTransactionSubmit({
        txHash,
        explorer,
        status: 'confirmed',
        fromChain,
        toChain,
        amount,
        blockNumber: confirmResponse.data.blockNumber
      });
    }
  } catch (error) {
    console.error('Error waiting for confirmation:', error);
    // Update transaction status to failed
    onTransactionSubmit({
      txHash,
      explorer,
      status: 'failed',
      fromChain,
      toChain,
      amount,
      error: error.message
    });
  }
};

  const handleMaxClick = () => {
    const maxAmount = parseFloat(balance) * 0.95; // Leave some for gas
    setAmount(maxAmount.toString());
  };

  const isValidForm = fromChain && toChain && amount && recipient && fromChain !== toChain;

  return (
    <div className="bridge-form">
      <h2>Bridge Your Assets</h2>

      <div className="form-group">
        <label>From</label>
        <select 
          value={fromChain} 
          onChange={(e) => setFromChain(e.target.value)}
          className="chain-select"
        >
          <option value="">Select source chain</option>
          {chains.map(chain => (
            <option key={chain.key} value={chain.key}>
              {chain.name}
            </option>
          ))}
        </select>
        <div className="balance">Balance: {balance}</div>
      </div>

      <div className="form-group">
        <label>To</label>
        <select 
          value={toChain} 
          onChange={(e) => setToChain(e.target.value)}
          className="chain-select"
        >
          <option value="">Select destination chain</option>
          {chains.map(chain => (
            <option key={chain.key} value={chain.key} disabled={chain.key === fromChain}>
              {chain.name}
            </option>
          ))}
        </select>
        <div className="balance">Balance: {toBalance}</div>
      </div>

      <div className="form-group">
        <label>Amount</label>
        <div className="amount-input">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.0"
            step="0.01"
            min="0"
          />
          <button 
            type="button" 
            onClick={handleMaxClick}
            className="max-btn"
          >
            MAX
          </button>
        </div>
      </div>

      <div className="form-group">
        <label>Recipient Address</label>
        <input
          type="text"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="Enter recipient address"
        />
      </div>

      {isValidForm && (
        <button 
          onClick={getQuote}
          disabled={loading}
          className="quote-btn"
        >
          {loading ? 'Getting Quote...' : 'Get Quote'}
        </button>
      )}

      {quote && (
        <div className="quote-section">
          <h3>Quote Details</h3>
          <div className="quote-item">
            <span>LayerZero Fee:</span>
            <span>{quote.formattedFee} {quote.currency}</span>
          </div>
          <div className="quote-item">
            <span>You will receive:</span>
            <span>{amount} tokens</span>
          </div>
          
          <button 
            onClick={executeTransfer}
            disabled={loading}
            className="transfer-btn"
          >
            {loading ? 'Executing...' : 'Execute Transfer'}
          </button>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};

export default BridgeForm;